// pages/lab/briefs/[slug].js
// ════════════════════════════════════════════════════════════════════
// Lab Weekly Brief — detail page (Phase F14).
// Renders one brief as a reading-shaped article: lede, what moved,
// watch metrics, predictions on deck, predictions resolved, what
// we're watching next, cited corpus.
// ════════════════════════════════════════════════════════════════════

import Head from 'next/head';
import Link from 'next/link';
import { BRIEFS, getBrief } from '../../../data/lab/briefs';
import { FindingCite } from '../../../features/findings/components-shared';
import { resolveShiftRef } from '../../../data/strategic-playbook/tectonic-shifts';

export async function getStaticPaths() {
  return {
    paths: BRIEFS.map((b) => ({ params: { slug: b.slug } })),
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const brief = getBrief(params.slug);
  if (!brief) return { notFound: true };
  return { props: { brief } };
}

export default function BriefDetailPage({ brief }) {
  return (
    <div className="sp-root">
      <Head>
        <title>{brief.shortTitle || brief.title} · Weekly Brief · Lab · Shift</title>
        <meta name="description" content={brief.description} />
      </Head>

      <article className="finding-article">
        <Link href="/lab/briefs" className="finding-back">← Weekly Briefs</Link>

        <header className="finding-header">
          <div className="finding-eyebrow">
            § Weekly Brief · {brief.weekOf} · published {brief.publishDate}
          </div>
          <h1 className="finding-title">{brief.title}</h1>
          {brief.description && (
            <p className="finding-sub">{brief.description}</p>
          )}
          <div className="brief-author-row">
            <span className="brief-author">By {brief.author}</span>
            <code className="brief-handle">{brief.citationHandle}</code>
          </div>
        </header>

        {/* Lede */}
        {brief.lede && (
          <section className="finding-lede">
            <p>{brief.lede}</p>
          </section>
        )}

        {/* What moved this week */}
        {brief.moved?.length > 0 && (
          <section className="finding-section">
            <div className="finding-section-eyebrow">§ What moved this week</div>
            <h2 className="finding-section-title">Across the corpus.</h2>
            <div className="brief-moved">
              {brief.moved.map((m, i) => (
                <MovedItem key={i} item={m} />
              ))}
            </div>
          </section>
        )}

        {/* Watch metrics */}
        {brief.watchMetrics?.length > 0 && (
          <section className="finding-section">
            <div className="finding-section-eyebrow">§ Watch metrics</div>
            <h2 className="finding-section-title">Where the numbers are.</h2>
            <table className="brief-metrics">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Value</th>
                  <th>Delta</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {brief.watchMetrics.map((m, i) => (
                  <tr key={i}>
                    <td>{m.metric}</td>
                    <td className="brief-metrics-value">{m.value}</td>
                    <td className="brief-metrics-delta">{m.delta || '—'}</td>
                    <td className="brief-metrics-source">{m.source || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Predictions on deck */}
        {brief.predictionsOnDeck?.length > 0 && (
          <section className="finding-section">
            <div className="finding-section-eyebrow">§ Predictions on the clock</div>
            <h2 className="finding-section-title">Resolutions approaching.</h2>
            <ol className="brief-predictions">
              {brief.predictionsOnDeck.map((p, i) => (
                <li key={i} className="brief-prediction">
                  <div className="brief-prediction-claim">{p.prediction}</div>
                  <div className="brief-prediction-meta">
                    Resolves: {p.resolutionDate}
                    {p.daysOut && <span className="brief-prediction-days"> · ~{p.daysOut} days out</span>}
                  </div>
                  {p.findingHandle && (
                    <div className="brief-prediction-source">
                      Source: <FindingCite handle={p.findingHandle} />
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Predictions resolved this week */}
        {brief.predictionsResolved?.length > 0 ? (
          <section className="finding-section">
            <div className="finding-section-eyebrow">§ Resolved this week</div>
            <h2 className="finding-section-title">Verdicts in.</h2>
            <ol className="brief-predictions">
              {brief.predictionsResolved.map((p, i) => (
                <li key={i} className="brief-prediction brief-prediction-resolved">
                  <div className="brief-prediction-claim">{p.prediction}</div>
                  <div className="brief-prediction-resolution">{p.resolution}</div>
                  {p.notes && <div className="brief-prediction-notes">{p.notes}</div>}
                  {p.findingHandle && (
                    <div className="brief-prediction-source">
                      Source: <FindingCite handle={p.findingHandle} />
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </section>
        ) : (
          brief.predictionsOnDeck?.length > 0 && (
            <section className="finding-section">
              <div className="finding-section-eyebrow">§ Resolved this week</div>
              <h2 className="finding-section-title">Nothing this period.</h2>
              <p className="finding-prose">
                No pre-registered predictions resolved this week. The next
                resolution windows are listed above.
              </p>
            </section>
          )
        )}

        {/* What we're watching next week */}
        {brief.watchingNext?.length > 0 && (
          <section className="finding-section">
            <div className="finding-section-eyebrow">§ What we&rsquo;re watching next week</div>
            <h2 className="finding-section-title">The radar.</h2>
            <ul className="finding-bullets">
              {brief.watchingNext.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Cited corpus */}
        {(brief.citedFindings?.length > 0 || brief.citedShifts?.length > 0) && (
          <section className="finding-section">
            <div className="finding-section-eyebrow">§ Cited this week</div>
            <h2 className="finding-section-title">The corpus underneath.</h2>

            {brief.citedFindings?.length > 0 && (
              <>
                <h3 className="finding-subhead">Findings</h3>
                <ul className="brief-cited-list">
                  {brief.citedFindings.map((handle) => (
                    <li key={handle}>
                      <FindingCite handle={handle} />
                    </li>
                  ))}
                </ul>
              </>
            )}

            {brief.citedShifts?.length > 0 && (
              <>
                <h3 className="finding-subhead">Tectonic shifts</h3>
                <ul className="brief-cited-list">
                  {brief.citedShifts.map((shiftId) => {
                    const ref = resolveShiftRef(shiftId);
                    return (
                      <li key={shiftId}>
                        {ref ? (
                          <Link href={`/strategic-playbook/tectonic/${ref.slug}`} className="methodology-inline-link">
                            {ref.name}
                          </Link>
                        ) : (
                          shiftId
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </section>
        )}

        <footer className="finding-footer">
          <div className="finding-footer-eyebrow">Brief ledger</div>
          <dl className="finding-meta">
            <dt>Artifact</dt><dd>Weekly Brief — cross-corpus synthesis, not a Lab finding</dd>
            <dt>Citation handle</dt><dd><code>{brief.citationHandle}</code></dd>
            <dt>Published</dt><dd>{brief.publishDate}</dd>
            <dt>Visibility</dt><dd>{brief.visibility || 'public-peek'}</dd>
            <dt>Distinction</dt><dd>Not pre-registered. Not falsifiable. Cites findings that are.</dd>
          </dl>
          <div className="finding-citation">
            <strong>Cite as:</strong> {brief.author} ({brief.publishDate.slice(0, 4)}). <em>{brief.title}</em>. Lab Weekly Brief <code>{brief.citationHandle}</code>.
          </div>
        </footer>
      </article>
    </div>
  );
}

function MovedItem({ item }) {
  const ref = item.shift ? resolveShiftRef(item.shift) : null;
  return (
    <div className="brief-moved-item">
      <div className="brief-moved-meta">
        {ref && (
          <Link href={`/strategic-playbook/tectonic/${ref.slug}`} className="brief-moved-shift">
            {ref.name}
          </Link>
        )}
        {item.finding && (
          <FindingCite handle={item.finding} />
        )}
      </div>
      <h3 className="brief-moved-headline">{item.headline}</h3>
      <p className="brief-moved-body">{item.body}</p>
    </div>
  );
}
